import { Label, Switch } from "@xola/ui-kit";
import { FormField } from "common";
import { produce } from "immer";
import { assignIn } from "lodash";
import React, { useEffect } from "react";
import {
    DownDeposit,
    DownDepositAmountType,
    DownDepositScope,
    DownDepositType,
    Experience,
    Merchandise,
    Package,
    PaymentMethod,
} from "types";
import { NumberInlineValuePopover } from "@/components/Common/NumberInlineValuePopover";
import { RadioInput } from "@/components/Common/RadioInput";
import { CurrencyInput } from "@/components/Input/CurrencyInput";
import { PercentInput } from "@/components/Input/PercentInput";
import { useModal } from "@/hooks/useVisibility";

const DownDepositTerms: { [key: string]: string } = {
    ABSOLUTE_PER_PERSON: `${DownDepositAmountType.ABSOLUTE}_${DownDepositScope.PERSON}`,
    ABSOLUTE_PER_OUTING: `${DownDepositAmountType.ABSOLUTE}_${DownDepositScope.OUTING}`,
    PERCENT_PER_OUTING: `${DownDepositAmountType.PERCENT}_${DownDepositScope.OUTING}`,
};

const getAmountMapIntialValues = () => {
    return {
        [DownDepositTerms.ABSOLUTE_PER_OUTING]: null,
        [DownDepositTerms.ABSOLUTE_PER_PERSON]: null,
        [DownDepositTerms.PERCENT_PER_OUTING]: null,
    };
};

let amountMap: { [key: string]: number | null } = getAmountMapIntialValues();

interface DownDepositSelectorProps {
    experience: Experience | Package | Merchandise;
    value: {
        downDeposit?: DownDeposit | null;
        paymentMethod?: PaymentMethod;
        balanceDueOffset?: number;
        balanceDueReminder?: boolean;
        autoChargeOnDepositReminder?: boolean;
    };
    errors: any;
    onChange: (key: string, value: any) => void;
    perPersonLabel?: string;
    perBookingLabel?: string;
    showDepositDays?: boolean;
    title?: string;
    description?: string;
}

export const DownDepositSelector = ({
    experience,
    value,
    errors,
    onChange,
    perPersonLabel = "per person",
    perBookingLabel = "per booking",
    showDepositDays = true,
    title,
    description,
}: DownDepositSelectorProps) => {
    const downDeposit = value.downDeposit ? value.downDeposit : null;
    const depositType = `${downDeposit?.amountType ?? ""}_${downDeposit?.scope ?? ""}`;
    const isExperience = "balanceDueOffset" in value;
    const balanceDueOffset = isExperience && value.balanceDueOffset ? value.balanceDueOffset / 1440 : 0;
    const isPayLater = value.paymentMethod === PaymentMethod.LATER;

    const balanceDueOffsetPopover = useModal();
    const downDepositDaysPopover = useModal();

    const defaultDownDepositValues = {
        type: DownDepositType.OPTIONAL,
        scope: DownDepositScope.PERSON,
        amountType: DownDepositAmountType.ABSOLUTE,
        days: 0,
    };

    const handleToggleDownDeposit = (enabled: boolean) => {
        // Resetting from experience since formik value will be null and we need to retain values
        const downDepositValue = experience.downDeposit ?? defaultDownDepositValues;
        onChange("downDeposit", enabled ? downDepositValue : null);
    };

    useEffect(() => {
        // setting only on inital time
        amountMap[depositType] = downDeposit?.amount ?? null;

        return () => {
            amountMap = getAmountMapIntialValues();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownDepositValueChange = (value: Partial<DownDeposit>) => {
        const currentDownDeposit = produce(downDeposit ? downDeposit : {}, (draft) => {
            assignIn(draft, value);
        }) as DownDeposit;
        const currentDepositType = `${currentDownDeposit?.amountType ?? ""}_${currentDownDeposit?.scope ?? ""}`;
        amountMap[currentDepositType] = value?.amount ?? null;
        onChange("downDeposit", currentDownDeposit);
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                {title && <div className="font-bold">{title}</div>}
                {description && <div className="text-sm">{description}</div>}
                <Switch.Group>
                    <Switch
                        disabled={isPayLater}
                        isChecked={!!downDeposit}
                        size="medium"
                        onChange={handleToggleDownDeposit}
                    />
                    <Switch.Label direction="right" className="ml-3">
                        Enable deposit payments&nbsp;
                        {downDeposit && showDepositDays ? (
                            <span onClick={(e) => e.stopPropagation()}>
                                if event is more than
                                <NumberInlineValuePopover
                                    isOpen={downDepositDaysPopover.isVisible}
                                    value={downDeposit?.days}
                                    text={downDeposit?.days.toString() ?? "0"}
                                    error={errors?.downDeposit?.days}
                                    onOpenChange={downDepositDaysPopover.set}
                                    onChange={(value: number) => handleDownDepositValueChange({ days: value })}
                                />
                                days away
                            </span>
                        ) : null}
                    </Switch.Label>
                </Switch.Group>
            </div>

            {downDeposit ? (
                <>
                    <FormField
                        className="w-96 space-y-2 text-gray-darker"
                        name="downDeposit.amount"
                        label="Deposit Amount"
                    >
                        <RadioInput
                            classNames={{ input: "mr-2" }}
                            checked={depositType === DownDepositTerms.ABSOLUTE_PER_PERSON}
                            onChange={() =>
                                handleDownDepositValueChange({
                                    amountType: DownDepositAmountType.ABSOLUTE,
                                    scope: DownDepositScope.PERSON,
                                    amount: amountMap[DownDepositTerms.ABSOLUTE_PER_PERSON] ?? undefined,
                                })
                            }
                        >
                            <span className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                                <span className="w-30" data-testid="down-deposit-selector-per-item-absolute-field">
                                    <CurrencyInput
                                        min={0}
                                        isError={
                                            depositType === DownDepositTerms.ABSOLUTE_PER_PERSON &&
                                            !!errors?.downDeposit?.amount
                                        }
                                        value={amountMap[DownDepositTerms.ABSOLUTE_PER_PERSON]}
                                        onFocus={() =>
                                            handleDownDepositValueChange({
                                                amountType: DownDepositAmountType.ABSOLUTE,
                                                scope: DownDepositScope.PERSON,
                                                amount: amountMap[DownDepositTerms.ABSOLUTE_PER_PERSON] ?? undefined,
                                            })
                                        }
                                        onValueChange={({ value }) =>
                                            depositType === DownDepositTerms.ABSOLUTE_PER_PERSON &&
                                            handleDownDepositValueChange({ amount: value ? Number(value) : undefined })
                                        }
                                    />
                                </span>
                                <span>{perPersonLabel}</span>
                            </span>
                        </RadioInput>
                        <RadioInput
                            classNames={{ input: "mr-2" }}
                            checked={depositType === DownDepositTerms.ABSOLUTE_PER_OUTING}
                            onChange={() =>
                                handleDownDepositValueChange({
                                    amountType: DownDepositAmountType.ABSOLUTE,
                                    scope: DownDepositScope.OUTING,
                                    amount: amountMap[DownDepositTerms.ABSOLUTE_PER_OUTING] ?? undefined,
                                })
                            }
                        >
                            <span className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                                <span className="w-30" data-testid="down-deposit-selector-per-booking-absolute-field">
                                    <CurrencyInput
                                        min={0}
                                        isError={
                                            depositType === DownDepositTerms.ABSOLUTE_PER_OUTING &&
                                            !!errors?.downDeposit?.amount
                                        }
                                        value={amountMap[DownDepositTerms.ABSOLUTE_PER_OUTING]}
                                        onFocus={() =>
                                            handleDownDepositValueChange({
                                                amountType: DownDepositAmountType.ABSOLUTE,
                                                scope: DownDepositScope.OUTING,
                                                amount: amountMap[DownDepositTerms.ABSOLUTE_PER_OUTING] ?? undefined,
                                            })
                                        }
                                        onValueChange={({ value }) =>
                                            depositType === DownDepositTerms.ABSOLUTE_PER_OUTING &&
                                            handleDownDepositValueChange({ amount: value ? Number(value) : undefined })
                                        }
                                    />
                                </span>
                                <span>{perBookingLabel}</span>
                            </span>
                        </RadioInput>
                        <RadioInput
                            classNames={{ input: "mr-2" }}
                            checked={depositType === DownDepositTerms.PERCENT_PER_OUTING}
                            onChange={() =>
                                handleDownDepositValueChange({
                                    amountType: DownDepositAmountType.PERCENT,
                                    scope: DownDepositScope.OUTING,
                                    amount: amountMap[DownDepositTerms.PERCENT_PER_OUTING] ?? undefined,
                                })
                            }
                        >
                            <span className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                                <span className="w-30">
                                    <PercentInput
                                        min={0}
                                        value={amountMap[DownDepositTerms.PERCENT_PER_OUTING]}
                                        isError={
                                            depositType === DownDepositTerms.PERCENT_PER_OUTING &&
                                            !!errors?.downDeposit?.amount
                                        }
                                        data-testid="down-deposit-selector-per-booking-percentage-field"
                                        onFocus={() =>
                                            handleDownDepositValueChange({
                                                amountType: DownDepositAmountType.PERCENT,
                                                scope: DownDepositScope.OUTING,
                                                amount: amountMap[DownDepositTerms.PERCENT_PER_OUTING] ?? undefined,
                                            })
                                        }
                                        onValueChange={({ value }) =>
                                            depositType === DownDepositTerms.PERCENT_PER_OUTING &&
                                            handleDownDepositValueChange({ amount: value ? Number(value) : undefined })
                                        }
                                    />
                                </span>
                                <span data-testid="down-deposit-selector-perbooking-percentage-span">
                                    {perBookingLabel}
                                </span>
                            </span>
                        </RadioInput>
                    </FormField>

                    <div className="space-y-2 text-gray-darker">
                        <Label>Checkout Preference</Label>
                        <RadioInput
                            checked={downDeposit?.type === DownDepositType.OPTIONAL}
                            name="checkoutPreference"
                            classNames={{ input: "mr-2" }}
                            onChange={() => handleDownDepositValueChange({ type: DownDepositType.OPTIONAL })}
                        >
                            <span>Allow customers to choose between paying in full or deposit amount</span>
                        </RadioInput>
                        <RadioInput
                            checked={downDeposit?.type === DownDepositType.REQUIRED}
                            name="checkoutPreference"
                            classNames={{ input: "mr-2" }}
                            onChange={() => handleDownDepositValueChange({ type: DownDepositType.REQUIRED })}
                        >
                            <span>Require customers to pay deposit amount only</span>
                        </RadioInput>
                    </div>
                </>
            ) : null}

            {isExperience ? (
                <>
                    <FormField name="balanceDueOffset" label="Balance Due Date">
                        Remaining balance is due
                        <NumberInlineValuePopover
                            isOpen={balanceDueOffsetPopover.isVisible}
                            value={balanceDueOffset ? balanceDueOffset : undefined}
                            text={balanceDueOffset.toString()}
                            error={errors?.balanceDueOffset}
                            onOpenChange={balanceDueOffsetPopover.set}
                            onChange={(value: number) => onChange("balanceDueOffset", value * 1440)}
                        />
                        day(s) before event
                    </FormField>

                    <div className="space-y-2 text-gray-darker">
                        <Label>Automatic Balance Collection</Label>
                        <RadioInput
                            checked={!value.balanceDueReminder && !value.autoChargeOnDepositReminder}
                            name="balanceCollection"
                            classNames={{ input: "mr-2" }}
                            onChange={() => {
                                onChange("balanceDueReminder", false);
                                onChange("autoChargeOnDepositReminder", false);
                            }}
                        >
                            <span>Do not automatically collect balance due</span>
                        </RadioInput>
                        <RadioInput
                            checked={!!value.balanceDueReminder && !value.autoChargeOnDepositReminder}
                            name="balanceCollection"
                            classNames={{ input: "mr-2" }}
                            onChange={() => {
                                onChange("balanceDueReminder", true);
                                onChange("autoChargeOnDepositReminder", false);
                            }}
                        >
                            <span>Send the customer an invoice on the due date</span>
                        </RadioInput>
                        <RadioInput
                            checked={!!value.autoChargeOnDepositReminder}
                            name="balanceCollection"
                            classNames={{ input: "mr-2" }}
                            disabled={isPayLater}
                            onChange={() => {
                                onChange("balanceDueReminder", false);
                                onChange("autoChargeOnDepositReminder", true);
                            }}
                        >
                            <span>Charge the first card in the purchase on the due date</span>
                        </RadioInput>
                    </div>
                </>
            ) : null}
        </div>
    );
};
